import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../utils/apiUtils';
import { config } from '../../config';

// Async thunks
export const fetchPageComments = createAsyncThunk(
    'pageComments/fetchPageComments',
    async ({ pageId }, { rejectWithValue }) => {
        try {
            const response = await api.get(`${config.API_URI}/v1/pages/${pageId}/comments`);
            return { pageId, comments: response.comments || [] };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const createPageComment = createAsyncThunk(
    'pageComments/createPageComment',
    async ({ pageId, comment, markId, quotedText }, { rejectWithValue }) => {
        try {
            const response = await api.post(`${config.API_URI}/v1/pages/${pageId}/comments`, {
                comment,
                markId,
                quotedText
            });
            return { pageId, comment: response.comment };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updatePageComment = createAsyncThunk(
    'pageComments/updatePageComment',
    async ({ pageId, commentId, comment }, { rejectWithValue }) => {
        try {
            const response = await api.put(`${config.API_URI}/v1/pages/${pageId}/comments/${commentId}`, {
                comment
            });
            return { pageId, comment: response.comment };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deletePageComment = createAsyncThunk(
    'pageComments/deletePageComment',
    async ({ pageId, commentId }, { rejectWithValue }) => {
        try {
            const response = await api.delete(`${config.API_URI}/v1/pages/${pageId}/comments/${commentId}`);
            return { pageId, commentId, markId: response.markId, shouldRemoveMark: response.shouldRemoveMark };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addReplyToComment = createAsyncThunk(
    'pageComments/addReply',
    async ({ pageId, commentId, comment }, { rejectWithValue }) => {
        try {
            const response = await api.post(`${config.API_URI}/v1/pages/${pageId}/comments/${commentId}/replies`, {
                comment
            });
            return { pageId, parentCommentId: commentId, reply: response.reply };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const resolveComment = createAsyncThunk(
    'pageComments/resolveComment',
    async ({ pageId, commentId }, { rejectWithValue }) => {
        try {
            const response = await api.post(`${config.API_URI}/v1/pages/${pageId}/comments/${commentId}/resolve`);
            return { pageId, comment: response.comment };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const pageCommentsSlice = createSlice({
    name: 'pageComments',
    initialState: {
        byPageId: {}, // { [pageId]: { comments: [], loading: false } }
        activeMarkId: null,
        sidebarOpen: false,
        showResolved: false,
        pendingMark: null, // { markId, quotedText } when user just created a mark but hasn't saved the comment yet
    },
    reducers: {
        setActiveMarkId: (state, action) => {
            state.activeMarkId = action.payload;
            if (action.payload) {
                state.sidebarOpen = true;
            }
        },
        toggleCommentsSidebar: (state) => {
            state.sidebarOpen = !state.sidebarOpen;
            if (!state.sidebarOpen) {
                state.activeMarkId = null;
                state.pendingMark = null;
            }
        },
        openCommentsSidebar: (state) => {
            state.sidebarOpen = true;
        },
        closeCommentsSidebar: (state) => {
            state.sidebarOpen = false;
            state.activeMarkId = null;
            state.pendingMark = null;
        },
        setPendingMark: (state, action) => {
            state.pendingMark = action.payload; // { markId, quotedText }
        },
        clearPendingMark: (state) => {
            state.pendingMark = null;
        },
        toggleShowResolved: (state) => {
            state.showResolved = !state.showResolved;
        },
        // Socket event handlers
        addCommentFromSocket: (state, action) => {
            const { pageId, comment } = action.payload;
            if (state.byPageId[pageId]) {
                const exists = state.byPageId[pageId].comments.some(c => c._id === comment._id);
                if (!exists) {
                    state.byPageId[pageId].comments.unshift({ ...comment, replies: [] });
                }
            }
        },
        updateCommentFromSocket: (state, action) => {
            const { pageId, comment } = action.payload;
            if (state.byPageId[pageId]) {
                const idx = state.byPageId[pageId].comments.findIndex(c => c._id === comment._id);
                if (idx !== -1) {
                    state.byPageId[pageId].comments[idx] = {
                        ...state.byPageId[pageId].comments[idx],
                        ...comment
                    };
                }
            }
        },
        deleteCommentFromSocket: (state, action) => {
            const { pageId, commentId } = action.payload;
            if (state.byPageId[pageId]) {
                state.byPageId[pageId].comments = state.byPageId[pageId].comments.filter(
                    c => c._id !== commentId
                );
            }
        },
        addReplyFromSocket: (state, action) => {
            const { pageId, parentCommentId, reply } = action.payload;
            if (state.byPageId[pageId]) {
                const parentIdx = state.byPageId[pageId].comments.findIndex(c => c._id === parentCommentId);
                if (parentIdx !== -1) {
                    const parent = state.byPageId[pageId].comments[parentIdx];
                    if (!parent.replies) parent.replies = [];
                    const exists = parent.replies.some(r => r._id === reply._id);
                    if (!exists) {
                        parent.replies.push(reply);
                    }
                }
            }
        },
        clearPageComments: (state, action) => {
            const pageId = action.payload;
            delete state.byPageId[pageId];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPageComments.pending, (state, action) => {
                const pageId = action.meta.arg.pageId;
                if (!state.byPageId[pageId]) {
                    state.byPageId[pageId] = { comments: [], loading: true };
                } else {
                    state.byPageId[pageId].loading = true;
                }
            })
            .addCase(fetchPageComments.fulfilled, (state, action) => {
                const { pageId, comments } = action.payload;
                state.byPageId[pageId] = { comments, loading: false };
            })
            .addCase(fetchPageComments.rejected, (state, action) => {
                const pageId = action.meta.arg.pageId;
                if (state.byPageId[pageId]) {
                    state.byPageId[pageId].loading = false;
                }
            })
            .addCase(createPageComment.fulfilled, (state, action) => {
                const { pageId, comment } = action.payload;
                if (!state.byPageId[pageId]) {
                    state.byPageId[pageId] = { comments: [], loading: false };
                }
                state.byPageId[pageId].comments.unshift({ ...comment, replies: [] });
                // Clear pending mark since the comment is now saved
                if (state.pendingMark && state.pendingMark.markId === comment.markId) {
                    state.pendingMark = null;
                }
            })
            // These thunks mutate state only on .fulfilled (no optimistic writes),
            // so a failure leaves the list unchanged. The .rejected cases exist so
            // a failed create keeps pendingMark (the user can retry) and so future
            // error-surfacing has a hook. The components show the toast.
            .addCase(createPageComment.rejected, (state) => {
                // keep state.pendingMark so the in-progress comment isn't lost
            })
            .addCase(addReplyToComment.rejected, (state) => {})
            .addCase(deletePageComment.rejected, (state) => {})
            .addCase(deletePageComment.fulfilled, (state, action) => {
                const { pageId, commentId } = action.payload;
                if (state.byPageId[pageId]) {
                    state.byPageId[pageId].comments = state.byPageId[pageId].comments.filter(
                        c => c._id !== commentId
                    );
                }
            })
            .addCase(addReplyToComment.fulfilled, (state, action) => {
                const { pageId, parentCommentId, reply } = action.payload;
                if (state.byPageId[pageId]) {
                    const parentIdx = state.byPageId[pageId].comments.findIndex(c => c._id === parentCommentId);
                    if (parentIdx !== -1) {
                        if (!state.byPageId[pageId].comments[parentIdx].replies) {
                            state.byPageId[pageId].comments[parentIdx].replies = [];
                        }
                        state.byPageId[pageId].comments[parentIdx].replies.push(reply);
                    }
                }
            })
            .addCase(resolveComment.fulfilled, (state, action) => {
                const { pageId, comment } = action.payload;
                if (state.byPageId[pageId]) {
                    const idx = state.byPageId[pageId].comments.findIndex(c => c._id === comment._id);
                    if (idx !== -1) {
                        state.byPageId[pageId].comments[idx] = {
                            ...state.byPageId[pageId].comments[idx],
                            isResolved: comment.isResolved,
                            resolvedBy: comment.resolvedBy,
                            resolvedAt: comment.resolvedAt
                        };
                    }
                }
            });
    }
});

export const {
    setActiveMarkId,
    toggleCommentsSidebar,
    openCommentsSidebar,
    closeCommentsSidebar,
    toggleShowResolved,
    setPendingMark,
    clearPendingMark,
    addCommentFromSocket,
    updateCommentFromSocket,
    deleteCommentFromSocket,
    addReplyFromSocket,
    clearPageComments,
} = pageCommentsSlice.actions;

export default pageCommentsSlice.reducer;
